import { useCallback, useState } from 'react';
import { AbstractCurrentSubscriptionModel } from '../../api/billing-types';
import dayjs from 'dayjs';
import {
  BillingKeys,
  getBillingInfoModelQuery,
  getHubSpotCompanyQuery,
  getLicenseNotificationModel,
  getPendingOrdersForType,
  getProductAssociations,
  getSubscriptions,
} from '../../api/billing-api';
import { useQueries, useQuery } from '@tanstack/react-query';
import AvatarChip from '../../shared/avatar-chip';
import { Messages } from '../../utils/message-constants';
import UpdateNotificationsForm from './notification-form';
import { useUserSettings } from '../../utils/use-user-settings';
import {
  BillingRoles,
  isAbleToSeeUIElement,
} from '../../utils/roles/client-roles';

import DriveIcon from '../../assets/subscriptions/drive.png';
import GeminiIcon from '../../assets/subscriptions/gemini.svg';
import GPanelIcon from '../../assets/subscriptions/gpanel.svg';
import GSuiteIcon from '../../assets/subscriptions/gsuite.svg';
import JamboardIcon from '../../assets/subscriptions/jamboard.png';
import VaultIcon from '../../assets/subscriptions/vault.png';
import { pagesConfig } from '../../shared/pages-config';
import { Outlet, useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { useRouteMatch } from '../../shared/useRouteMatch';
import ValidateHubspotContactDialog from './validate-hubspot-contact/validate-hubspot-contact-dialog';
import { validatePrimaryBillingContact } from '../../shared/hubspot-address-utils';
import PurchaseLicenseDialog from './purchase-license-dialog';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import NotificationAdd from '@mui/icons-material/NotificationAdd';
import PersonAdd from '@mui/icons-material/PersonAdd';
import SharedTabHeader from '../../shared/shared-tab-header';

export default function SubscriptionsPage() {
  const navigate = useNavigate();
  const { billingRoles } = useUserSettings();
  const { orderNumber: orderNumberFromUrl } = useParams();
  const [orderNumber] = useState<string | null>(orderNumberFromUrl || null);
  const [openBillingConfirmationDialog, setOpenBillingConfirmationDialog] =
    useState(!!orderNumberFromUrl);
  const [
    openValidateHubspotContactDialog,
    setOpenValidateHubspotContactDialog,
  ] = useState(false);
  const [openNotificationDialog, setOpenNotificationDialog] = useState(false);
  const [selectedSubscriptionModel, setSelectedSubscriptionModel] =
    useState<AbstractCurrentSubscriptionModel | null>(null);
  const [currentRoute, setCurrentRoute] = useState<string>('');

  const { isLoading, data } = useQuery({
    queryKey: [BillingKeys.ProductAssociations],
    queryFn: () => getProductAssociations(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('product associations'),
    },
  });

  const { isLoading: isLoadingSubs, data: subscriptionsRecord } = useQuery({
    queryKey: [BillingKeys.Subscriptions],
    queryFn: () => getSubscriptions(data!),
    enabled: !!data,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('subscriptions'),
    },
  });

  const subscriptions = subscriptionsRecord?.subscriptions;
  const hasBoughtWorkspaceThroughPromevo =
    (subscriptionsRecord?.totalPurchasedAULicenses || 0) > 0 ||
    (subscriptionsRecord?.totalPurchasedWorkspaceLicenses || 0) > 0;

  const index =
    subscriptions?.findIndex(
      (sub) =>
        sub.productModel.productName ===
        selectedSubscriptionModel?.productModel.productName
    ) ?? -1;

  const results = useQueries({
    queries: subscriptions
      ? subscriptions.map((sub) => {
          return {
            queryKey: [BillingKeys.PendingOrders, sub.productModel],
            queryFn: () => getPendingOrdersForType(sub.productModel.product),
          };
        })
      : [],
  });

  const notificationResults = useQueries({
    queries: subscriptions
      ? subscriptions.map((sub) => ({
          queryKey: [BillingKeys.LicenseNotificationModel, sub.productModel],
          queryFn: () => getLicenseNotificationModel(sub.productModel.product),
        }))
      : [],
  });

  const { isLoading: isLoadingBillingInfo, data: billingInformationModel } =
    useQuery(getBillingInfoModelQuery());

  const { isLoading: isLoadingHubSpotCompany, data: hubSpotCompany } = useQuery(
    getHubSpotCompanyQuery()
  );

  const isAbleToAddLicenses = isAbleToSeeUIElement(
    billingRoles,
    BillingRoles.canAddLicenses.value
  );

  const getImage = useCallback((imageUrl: string) => {
    const url = imageUrl.toLowerCase();

    if (url.includes('gsuite')) {
      return GSuiteIcon;
    } else if (url.includes('gpanel')) {
      return GPanelIcon;
    } else if (url.includes('jamboard')) {
      return JamboardIcon;
    } else if (url.includes('vault')) {
      return VaultIcon;
    } else if (url.includes('drive')) {
      return DriveIcon;
    } else if (url.includes('gemini')) {
      return GeminiIcon;
    } else {
      return GSuiteIcon;
    }
  }, []);

  const descriptionText =
    'You can add additional licenses to your subscriptions by clicking on the "Add License" buttons below.';

  const addLicenseRoute = `${pagesConfig.billing.link}/${pagesConfig.billingSubscriptions.link}/${pagesConfig.addLicense.link}`;
  const isFormPage = useRouteMatch([addLicenseRoute]);

  if (isFormPage) return <Outlet />;

  if (
    (isLoadingSubs ||
      isLoadingBillingInfo ||
      isLoading ||
      isLoadingHubSpotCompany ||
      !billingInformationModel ||
      !hubSpotCompany) &&
    !isFormPage
  ) {
    return (
      <Stack justifyContent="center" sx={{ width: '100%' }}>
        <SharedTabHeader title="Subscriptions" />
        <Typography variant="body1" sx={{ mt: 2, px: 1 }}>
          {descriptionText}
        </Typography>
        <Grid container spacing={1} sx={{ my: 2, px: 2 }}>
          {Array.from({ length: 5 }).map((_, index) => {
            return (
              <Grid size={{ md: 12 }} key={index}>
                <Card sx={{ width: '100%' }}>
                  <CardContent
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 1,
                      p: 2,
                    }}
                  >
                    <Skeleton variant="rectangular" height={100} width={100} />

                    <Box sx={{ width: '100%' }}>
                      <Stack
                        flexDirection="row"
                        justifyContent="space-between"
                        gap={1}
                      >
                        <Skeleton variant="text" width={100} />
                        <Skeleton variant="text" width={80} />
                      </Stack>

                      <Stack flexDirection="row" gap={1}>
                        <Skeleton variant="text" height={20} width={100} />
                        <Divider orientation="vertical" flexItem />
                        <Skeleton variant="text" height={20} width={100} />
                      </Stack>

                      <Stack flexDirection="row" gap={1} sx={{ mt: 3 }}>
                        <Skeleton variant="text" height={20} width={100} />
                        <Divider orientation="vertical" flexItem />
                        <Skeleton variant="text" height={20} width={100} />
                        <Divider orientation="vertical" flexItem />
                        <Skeleton variant="text" height={20} width={100} />
                        <Divider orientation="vertical" flexItem />
                        <Skeleton variant="text" height={20} width={100} />
                        <Divider orientation="vertical" flexItem />
                        <Skeleton variant="text" height={20} width={100} />
                        <Divider orientation="vertical" flexItem />
                        <Skeleton variant="text" height={20} width={100} />
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Stack>
    );
  }

  if (
    !isFormPage &&
    !isLoadingSubs &&
    !isLoadingBillingInfo &&
    !isLoading &&
    !isLoadingHubSpotCompany &&
    billingInformationModel &&
    hubSpotCompany
  )
    return (
      <Stack justifyContent="center" sx={{ width: '100%' }}>
        <SharedTabHeader title="Subscriptions" />
        <Typography variant="body1" sx={{ mt: 2, px: 1 }}>
          {descriptionText}
        </Typography>

        <Grid
          container
          spacing={1}
          sx={{ my: 2, px: 2 }}
          justifyContent={'center'}
        >
          {subscriptions
            ?.filter((sub) => {
              if (hasBoughtWorkspaceThroughPromevo) {
                return !sub.productModel.productName
                  .toLowerCase()
                  .includes('gpanel');
              }
              return true;
            })
            .map((sub, index) => {
              const freeGpanelProduct =
                sub.productModel.productName in data! &&
                data![sub.productModel.productName].isFreeProduct;
              const isGSuiteEdu =
                sub.productModel.product === 'GSuiteEnterpriseForEducation' ||
                sub.productModel.product ===
                  'GSuiteEnterpriseForEducationStudent';
              const subscriptionOrders = results
                .map((queryResult) => queryResult.data)
                .flat()
                .filter(
                  (purchaseOrder) =>
                    purchaseOrder?.licType === sub.productModel.productName
                );

              return (
                <Grid size={{ md: 12 }} key={sub.productModel.productName}>
                  <Card sx={{ width: '100%' }}>
                    <CardContent
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 1,
                        p: 2,
                      }}
                    >
                      <img
                        src={getImage(sub.productModel.productImageUrl)}
                        alt={sub.productModel.productName}
                        style={{
                          width: 32,
                          height: 32,
                          marginTop: 6,
                        }}
                      />

                      <Box sx={{ width: '100%' }}>
                        <Stack
                          flexDirection="row"
                          justifyContent="space-between"
                          gap={1}
                        >
                          <Typography sx={{ fontWeight: 'bold' }}>
                            {sub.productModel.productName}
                          </Typography>
                          <Chip
                            label={sub.subscriptionStatus}
                            size="small"
                            color={
                              sub.subscriptionStatus === 'Active'
                                ? 'success'
                                : 'info'
                            }
                            variant="outlined"
                          />
                        </Stack>

                        <Stack flexDirection="row" gap={1}>
                          {freeGpanelProduct ? (
                            <Tooltip
                              arrow
                              title={
                                'For each active Google Workspace license in your domain, there is a corresponding gPanel license.'
                              }
                            >
                              <Typography variant="caption">
                                Purchased Licenses: Domain-Wide License Coverage
                              </Typography>
                            </Tooltip>
                          ) : (
                            <>
                              <Typography variant="caption">
                                Purchased Licenses: {sub.totalPurchasedLicenses}
                              </Typography>
                              {sub.commitmentPlan !== 'Flexible' &&
                              sub.licensedUsers ? (
                                <>
                                  <Divider orientation="vertical" flexItem />
                                  <Typography variant="caption">
                                    Licensed Users: {sub.licensedUsers}
                                  </Typography>
                                </>
                              ) : null}
                            </>
                          )}
                        </Stack>

                        <Stack flexDirection="row" gap={1} sx={{ mt: 1 }}>
                          <Typography variant="caption">
                            {sub.billingPlan}
                          </Typography>
                          <Divider orientation="vertical" flexItem />
                          {billingInformationModel!.qbInstance === 'us' ? (
                            <Typography variant="caption">
                              Currency: USD
                            </Typography>
                          ) : (
                            <Typography variant="caption">
                              Currency: CAD
                            </Typography>
                          )}
                          {sub.commitmentPlan !== 'Flexible' ? (
                            <>
                              <Divider orientation="vertical" flexItem />
                              <Typography variant="caption">
                                {new Intl.NumberFormat('en-US', {
                                  style: 'currency',
                                  currency:
                                    billingInformationModel!.qbInstance === 'us'
                                      ? 'USD'
                                      : 'CAD',
                                }).format(sub.pricePerUnit)}
                                /user
                              </Typography>
                              <Divider orientation="vertical" flexItem />
                            </>
                          ) : null}
                          {sub.commitmentPlan !== 'Flexible' ? (
                            <>
                              <Typography variant="caption">
                                {sub.billingType === 'annualPrepay'
                                  ? `Est. annual bill: ${new Intl.NumberFormat(
                                      'en-US',
                                      {
                                        style: 'currency',
                                        currency:
                                          billingInformationModel!
                                            .qbInstance === 'us'
                                            ? 'USD'
                                            : 'CAD',
                                      }
                                    ).format(sub.totalLicenseCost)}`
                                  : `Est. monthly bill: ${new Intl.NumberFormat(
                                      'en-US',
                                      {
                                        style: 'currency',
                                        currency:
                                          billingInformationModel!
                                            .qbInstance === 'us'
                                            ? 'USD'
                                            : 'CAD',
                                      }
                                    ).format(sub.totalLicenseCost)}`}
                              </Typography>
                            </>
                          ) : null}
                          {sub.commitmentPlan !== 'Flexible' && sub.timeout ? (
                            <>
                              <Divider orientation="vertical" flexItem />
                              <Typography variant="caption">
                                Expires{' '}
                                {sub.timeout
                                  ? dayjs(sub.timeout)
                                      .format('MM/DD/YYYY')
                                      .toString()
                                  : ''}
                              </Typography>
                            </>
                          ) : sub.commitmentPlan === 'Flexible' ? (
                            <>
                              <Divider orientation="vertical" flexItem />
                              <Typography variant="caption">
                                Flexible Plan
                              </Typography>
                            </>
                          ) : null}
                        </Stack>
                      </Box>
                    </CardContent>
                    {subscriptionOrders && subscriptionOrders.length > 0 ? (
                      <>
                        <Divider sx={{ mx: 2, mb: 1 }} />
                        <Stack gap={1}>
                          {subscriptionOrders?.map((pendingOrder) => {
                            return pendingOrder ? (
                              <Alert
                                sx={{ width: 'fit-content', ml: 2 }}
                                key={pendingOrder.key}
                                severity="info"
                              >
                                Pending Order #{pendingOrder.orderNumber}:{' '}
                                <AvatarChip
                                  email={pendingOrder.purchasedBy}
                                  type="User"
                                />{' '}
                                placed an order for {pendingOrder.licenseCount}{' '}
                                {pendingOrder.licenseCount > 1
                                  ? 'additional licenses'
                                  : 'additional license'}{' '}
                                on{' '}
                                {new Date(
                                  pendingOrder.orderDate
                                ).toLocaleDateString()}
                                .
                              </Alert>
                            ) : null;
                          })}
                        </Stack>
                      </>
                    ) : null}
                    <CardActions sx={{ justifyContent: 'flex-end', gap: 1 }}>
                      <Tooltip
                        title={
                          notificationResults[index].data?.isActive
                            ? 'Notifications enabled'
                            : 'Update notification settings'
                        }
                        placement="bottom"
                        arrow
                      >
                        <IconButton
                          onClick={() => {
                            setSelectedSubscriptionModel(sub);
                            setOpenNotificationDialog(true);
                          }}
                        >
                          <NotificationAdd
                            color={
                              notificationResults[index].data?.isActive
                                ? 'primary'
                                : 'inherit'
                            }
                          />
                        </IconButton>
                      </Tooltip>

                      <Tooltip
                        arrow
                        title={
                          isGSuiteEdu
                            ? `To purchase additional license(s) please contact salessupport@promevo.com `
                            : sub.commitmentPlan === 'Flexible'
                            ? `You're billed monthly for each user account. You can add and remove accounts at any time and pay only for the accounts you use during that month. You can cancel service at any time without penalty.`
                            : !isAbleToAddLicenses
                            ? Messages.Errors.Permission.Acting(
                                BillingRoles.canAddLicenses.name
                              )
                            : ''
                        }
                      >
                        <span>
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            disabled={
                              sub.commitmentPlan === 'Flexible' ||
                              !sub.ableToAddLicenses ||
                              isGSuiteEdu ||
                              !isAbleToAddLicenses
                            }
                            onClick={() => {
                              setSelectedSubscriptionModel(sub);
                              const route = `/${pagesConfig.billing.link}/${
                                pagesConfig.billingSubscriptions.link
                              }/${pagesConfig.addLicense.link.replace(
                                ':id',
                                index.toString()
                              )}`;
                              setCurrentRoute(route);

                              try {
                                validatePrimaryBillingContact(
                                  billingInformationModel
                                );
                                navigate(route);
                              } catch {
                                setOpenValidateHubspotContactDialog(true);
                              }
                            }}
                            endIcon={<PersonAdd />}
                          >
                            Add License
                          </Button>
                        </span>
                      </Tooltip>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
        </Grid>
        {openNotificationDialog &&
        selectedSubscriptionModel &&
        hubSpotCompany &&
        notificationResults[index].data ? (
          <UpdateNotificationsForm
            open={openNotificationDialog}
            handleClose={() => setOpenNotificationDialog(false)}
            subscription={selectedSubscriptionModel}
            licenseNotificationModel={notificationResults[index].data}
            hubSpotCompany={hubSpotCompany}
          />
        ) : null}
        {openValidateHubspotContactDialog && hubSpotCompany ? (
          <ValidateHubspotContactDialog
            open={openValidateHubspotContactDialog}
            handleClose={() => setOpenValidateHubspotContactDialog(false)}
            billingInformationModel={billingInformationModel}
            currentRoute={currentRoute}
            hubSpotCompany={hubSpotCompany}
          />
        ) : null}
        {openBillingConfirmationDialog && (
          <PurchaseLicenseDialog
            open={openBillingConfirmationDialog}
            handleClose={() => setOpenBillingConfirmationDialog(false)}
            orderNumber={orderNumber}
          />
        )}
      </Stack>
    );
}
